/**
 * TodaysGamePlanStrip — three glance-pills under the Win The Day hero
 * answering "what matters today beyond emails?" — Today's Focus,
 * Active Contest, Next Event. Each pill is read-only and deep-links
 * to the page where the data is actually edited / explored:
 *
 *   • Focus  → Zone Sheet (where `store_focus` is set)
 *   • Contest → Contests page
 *   • Event  → What's Up (calendar)
 *
 * Zero new schema. Reads existing `store_focus`, contest summary,
 * and events list that the dashboard already fetches. Pills hide
 * individually when their underlying source is empty — the strip
 * itself collapses if all three are empty.
 *
 * (User ask 2026-06-13 Wave A.5 — should have been in Wave A.)
 */
import { Target, Trophy, CalendarBlank, ArrowRight } from "@phosphor-icons/react";

function dayLabel(iso) {
  if (!iso) return "";
  try {
    const d = new Date(`${iso}T12:00:00`);
    // MST-anchored today/tomorrow so labels don't flip to "Tomorrow" at
    // 5 PM MST when UTC rolls over. Same fix class as HomeScreen Right
    // Now reset (2026-02-27).
    const ymd = (x) => x.toLocaleDateString("en-CA", { timeZone: "America/Denver" });
    const today = new Date();
    const todayYmd = ymd(today);
    if (iso === todayYmd) return "Today";
    const tomorrow = new Date(today);
    tomorrow.setDate(today.getDate() + 1);
    if (iso === ymd(tomorrow)) return "Tomorrow";
    return d.toLocaleDateString([], { month: "short", day: "numeric" });
  } catch {
    return iso;
  }
}

function Pill({ tone, eyebrow, title, subtitle, Icon, onClick, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex-1 min-w-[180px] flex items-center gap-2.5 rounded-2xl px-3 py-2.5 text-left ring-1 transition-all hover:-translate-y-px hover:shadow-md ${tone}`}
      data-testid={testid}
    >
      <span className="inline-flex items-center justify-center w-9 h-9 rounded-2xl text-white shadow-md shrink-0 bg-white/30 backdrop-blur-sm">
        <Icon weight="fill" className="w-4 h-4" />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-[9px] font-black uppercase tracking-[0.22em] opacity-80 leading-none">
          {eyebrow}
        </span>
        <span className="block text-sm font-black leading-tight mt-1 truncate" style={{ fontFamily: "'Five Below Gothic', Chivo, sans-serif" }}>
          {title}
        </span>
        {subtitle && (
          <span className="block text-[10px] font-bold opacity-80 mt-0.5 truncate">
            {subtitle}
          </span>
        )}
      </span>
      <ArrowRight weight="bold" className="w-4 h-4 opacity-70 shrink-0" />
    </button>
  );
}

export default function TodaysGamePlanStrip({
  todayFocus,
  contestSummary,
  nextEvent,
  onGoToZoneSheet,
  onGoToContests,
  onGoToCalendar,
}) {
  const hasFocus = !!(todayFocus && todayFocus.trim());
  const hasContest = contestSummary && contestSummary.is_active;
  const hasEvent = !!nextEvent;
  if (!hasFocus && !hasContest && !hasEvent) return null;

  return (
    <section
      data-testid="todays-game-plan-strip"
      className="flex flex-wrap gap-2 sm:gap-3"
    >
      {hasFocus && (
        <Pill
          tone="bg-gradient-to-br from-amber-400 via-orange-400 to-pink-500 ring-orange-200 text-white"
          eyebrow="Today's Focus"
          title={todayFocus}
          Icon={Target}
          onClick={onGoToZoneSheet}
          testid="game-plan-focus"
        />
      )}
      {hasContest && (
        <Pill
          tone="bg-gradient-to-br from-violet-500 via-fuchsia-500 to-pink-500 ring-violet-300 text-white"
          eyebrow="Active Contest"
          title={contestSummary.name || "Email Contest"}
          subtitle={
            contestSummary.days_left > 0
              ? `${contestSummary.days_left}d left · $${contestSummary.prize_per_crew} prize`
              : null
          }
          Icon={Trophy}
          onClick={onGoToContests}
          testid="game-plan-contest"
        />
      )}
      {hasEvent && (
        <Pill
          tone="bg-gradient-to-br from-blue-500 via-cyan-500 to-teal-500 ring-blue-300 text-white"
          eyebrow="Next Event"
          title={nextEvent.title || nextEvent.name || "Event"}
          subtitle={dayLabel(nextEvent.date)}
          Icon={CalendarBlank}
          onClick={onGoToCalendar}
          testid="game-plan-event"
        />
      )}
    </section>
  );
}
